"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Safari 26 on iOS ignores `theme-color` and fills the area behind the status
 * bar with the colour of a fixed element at the top of the viewport, or the
 * page background if there is none. This strip, in the header colour, is that
 * element, so the header colour runs up behind the status bar and stays there
 * after the header has scrolled away. Safari ignores fixed elements under
 * about 6px.
 *
 * Other browsers would show the strip as a line over the content, so there it
 * is only shown while it lies on top of the header bar. Render it as a direct
 * child of the header bar.
 */
export function HeaderStatusBarTint({
  initiallyVisible,
}: {
  initiallyVisible: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(initiallyVisible);

  useEffect(() => {
    const headerBar = ref.current?.parentElement;
    if (!headerBar) return;
    const keepAfterHeader = isIosSafari26OrNewer(navigator);
    // A zero-height root along the top edge of the viewport: the header bar
    // intersects it while it covers that edge.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const scrolledPast = entry.boundingClientRect.bottom <= 0;
        setVisible(entry.isIntersecting || (keepAfterHeader && scrolledPast));
      },
      { rootMargin: "0px 0px -100% 0px" },
    );
    observer.observe(headerBar);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-10 h-1.5 bg-[var(--cvr-header-background)]"
      style={visible ? undefined : { display: "none" }}
    />
  );
}

function isIosSafari26OrNewer({ userAgent, maxTouchPoints }: Navigator) {
  // iPadOS Safari reports itself as a Mac.
  const isIos =
    /iP(hone|ad|od)/.test(userAgent) ||
    (userAgent.includes("Macintosh") && maxTouchPoints > 1);
  // Other iOS browsers add their own token, and in-app web views lack "Safari/".
  const isSafari =
    userAgent.includes("Safari/") &&
    !/CriOS|FxiOS|EdgiOS|OPiOS/.test(userAgent);
  // The iOS version in the user agent is frozen, so read Safari's own version.
  const version = Number(userAgent.match(/Version\/(\d+)/)?.[1] ?? 0);
  return isIos && isSafari && version >= 26;
}
